// Separate terminal measurement ages even when only one timepoint exists.
// Preserve all original nodes/links for edge evidence and the details drawer.
export function temporalIdentity(node) {
  if (!(node.category_tags || [node.type]).includes('Attributes & Measurements')) return null;
  const name = node.name || node.title || node.id;
  const ages = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };
  const match = name.match(/^(.*?)\s+at\s+(age\s+)?(\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)(?:\s+(years?|months?))?$/i);
  if (!match || (!match[2] && !match[4])) return null;
  const age = ages[match[3].toLowerCase()] || match[3];
  return { base: match[1], timepoint: `${age} ${match[4] || 'years'}` };
}
export function groupTemporalGraph(data) {
  const groups = new Map();
  for (const node of data.nodes) {
    const temporal = temporalIdentity(node);
    const key = temporal ? JSON.stringify([node.type, [...(node.category_tags || [node.type])].sort(), temporal.base.toLowerCase()]) : node.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(node);
  }
  const endpoints = new Map();
  const nodes = [...groups.values()].map(originals => {
    const first = originals[0];
    const grouped = originals.length > 1;
    const name = temporalIdentity(first)?.base || first.name;
    const node = { ...first, id: grouped ? `timegroup:${first.id}` : first.id, name, title: name || first.title,
      underlyingNodes: originals, neighbors: [], links: [],
      measurement_timepoints: [...new Set([...originals.flatMap(n => n.measurement_timepoints || []), ...originals.map(temporalIdentity).filter(Boolean).map(temporal => temporal.timepoint)])],
      text_unit_ids: [...new Set(originals.flatMap(n => n.text_unit_ids || []))],
      aliases: [...new Set(originals.flatMap(n => n.aliases || []))],
      study_cohorts: [...new Set(originals.flatMap(n => n.study_cohorts || []))],
      studies: [...new Set(originals.flatMap(n => n.studies || []))],
      cohorts: [...new Set(originals.flatMap(n => n.cohorts || []))],
      attribute_timepoints: [...new Set(originals.flatMap(n => n.attribute_timepoints || []))],
      description: grouped ? originals.map(n => `${n.name}: ${n.description || ''}`).join('\n\n') : first.description };
    originals.forEach(n => endpoints.set(n.id, node));
    return node;
  });
  const pairs = new Map();
  const id = value => typeof value === 'object' ? value.id : value;
  for (const link of data.links) {
    const source = endpoints.get(id(link.source));
    const target = endpoints.get(id(link.target));
    if (!source || !target) continue;
    const key = JSON.stringify([source.id, target.id].sort());
    if (!pairs.has(key)) pairs.set(key, { ...link, source, target, originalLinks: [], weight: 0, descriptions: [], text_unit_ids: [] });
    const edge = pairs.get(key);
    edge.originalLinks.push(link);
    edge.weight += Number(link.weight) || 1;
    edge.descriptions.push(link.description || '');
    edge.text_unit_ids.push(...(link.text_unit_ids || []));
  }
  const links = [...pairs.values()].map(edge => {
    const { descriptions, ...link } = edge;
    link.description = descriptions.join('\n');
    link.text_unit_ids = [...new Set(link.text_unit_ids)];
    for (const [node, neighbor] of [[link.source, link.target], [link.target, link.source]]) {
      if (!node.neighbors.includes(neighbor)) node.neighbors.push(neighbor);
      if (!node.links.includes(link)) node.links.push(link);
    }
    return link;
  });
  return { nodes, links };
}
export function underlyingPairKey(source, target) {
  return JSON.stringify([source, target].sort());
}
export function recordsForLink(link, provenance) {
  const originals = link.originalLinks || [link];
  if (originals.some(original => original.displayRecords)) {
    return originals.flatMap(original => original.displayRecords || []);
  }
  if (originals.some(original => original.statisticalRecords)) {
    return originals.flatMap(original => original.statisticalRecords || []);
  }
  if (!provenance) return (link.description || '').split('\n').flatMap(line => {
    try { return [JSON.parse(line)]; } catch { return []; }
  });
  const titles = new Map(provenance.entities.map(e => [e.entity_id, e.graph_title]));
  const names = new Map(provenance.entities.map(e => [e.entity_id, e.name]));
  const endpoint = node => typeof node === 'object' ? node.id : node;
  const pairs = new Set((link.originalLinks || [link]).map(l => underlyingPairKey(endpoint(l.source), endpoint(l.target))));
  return provenance.relationships.filter(r => pairs.has(underlyingPairKey(titles.get(r.source), titles.get(r.target))))
    .map(r => ({ ...r, source: names.get(r.source), target: names.get(r.target) }));
}
